import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CommunityService from '../../lib/services/communityService';
import NotificationService, { SystemNotification } from '../../lib/services/notificationService';
import type { UserProfile, UserConnection } from '../../types';
import { MessageSquare, ChevronRight, Search, User, Bell } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Spinner } from '../../components/Spinner';
import SEO from '../../components/SEO';

// Criar instâncias dos serviços
const communityService = new CommunityService();
const notificationService = new NotificationService();

/**
 * Página principal de mensagens que lista todas as conversas do usuário
 */
const MessagesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState<UserConnection[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [unreadCountMap, setUnreadCountMap] = useState<Record<string, number>>({});
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;

      setLoading(true);
      setLoadingNotifications(true);
      
      try {
        // Carregar conexões aceitas (amigos)
        const acceptedConnections = await communityService.getAcceptedConnections(user.id);
        setConnections(acceptedConnections);

        // Carregar notificações não lidas (limitado a 3)
        const unreadNotifications = await notificationService.getUnreadNotifications(user.id, 3);
        setNotifications(unreadNotifications);

        // Carregar contagem de mensagens não lidas para cada conversa
        const unreadMap: Record<string, number> = {};
        for (const connection of acceptedConnections) {
          const otherUserId = connection.connected_profile?.id;
          if (otherUserId) {
            const messages = await communityService.getMessages(user.id, otherUserId);
            const unreadCount = messages.filter(msg => 
              msg.recipient_id === user.id && !msg.read
            ).length;
            
            unreadMap[otherUserId] = unreadCount;
          }
        }
        setUnreadCountMap(unreadMap);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        toast.error('Não foi possível carregar os dados');
      } finally {
        setLoading(false);
        setLoadingNotifications(false);
      }
    };

    loadData();
  }, [user?.id]);

  // Filtra as conexões com base no termo de pesquisa
  const filteredConnections = connections.filter(conn => {
    const userName = conn.connected_profile?.username?.toLowerCase() || '';
    const fullName = conn.connected_profile?.full_name?.toLowerCase() || '';
    const searchTermLower = searchTerm.toLowerCase();
    
    return userName.includes(searchTermLower) || fullName.includes(searchTermLower);
  });

  const handleStartConversation = (username?: string) => {
    if (!username) return;
    navigate(`/messages/${username}`);
  };

  const handleMarkNotificationAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId, user!.id);
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      toast.error('Erro ao marcar notificação como lida');
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    try {
      await notificationService.markAllAsRead(user!.id);
      setNotifications([]);
    } catch (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
      toast.error('Erro ao marcar notificações como lidas');
    }
  };

  return (
    <>
      <SEO 
        title="Mensagens"
        description="Suas conversas com outros usuários do Boardverso."
      />
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">Notificações e Mensagens</h1>
          <p className="text-gray-600 dark:text-gray-400">Acompanhe suas notificações e converse com seus amigos</p>
        </div>

        {/* Seção de Notificações do Sistema */}
        {loadingNotifications ? (
          <div className="mb-6">
            <Spinner />
          </div>
        ) : notifications.length > 0 ? (
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 dark:text-gray-100">
                <Bell className="h-5 w-5" />
                Notificações
              </h2>
              <div className="flex items-center gap-4">
                {notifications.length > 1 && (
                  <button
                    onClick={handleMarkAllNotificationsAsRead}
                    className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    Marcar todas como lidas
                  </button>
                )}
                <Link
                  to="/notifications"
                  className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Ver todas
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="space-y-3">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 dark:bg-gray-800 dark:border-gray-700"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">{notification.title}</h3>
                      <p className="text-gray-600 mt-1 text-sm dark:text-gray-400">{notification.body}</p>
                      {notification.url && (
                        <a
                          href={notification.url}
                          className="text-indigo-600 hover:text-indigo-800 text-sm mt-2 inline-block dark:text-indigo-400 dark:hover:text-indigo-300"
                        >
                          Saiba mais
                        </a>
                      )}
                    </div>
                    <button
                      onClick={() => handleMarkNotificationAsRead(notification.id)}
                      className="text-gray-400 hover:text-gray-600 ml-4 dark:text-gray-500 dark:hover:text-gray-300"
                    >
                      <span className="sr-only">Marcar como lida</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {formatDistanceToNow(new Date(notification.created_at), { 
                      addSuffix: true,
                      locale: ptBR 
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 dark:text-gray-100">
                <Bell className="h-5 w-5" />
                Notificações
              </h2>
              <Link
                to="/notifications"
                className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Ver histórico
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <p className="text-gray-500 mt-2 dark:text-gray-400">Nenhuma notificação recente.</p>
          </div>
        )}

        {/* Seção de Mensagens */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 dark:text-gray-100">
              <MessageSquare className="h-5 w-5" />
              Mensagens
            </h2>
          </div>

          {/* Barra de pesquisa */}
          <div className="relative mb-6">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Pesquisar amigos..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:ring-indigo-500 dark:focus:border-indigo-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-500"></div>
            </div>
          ) : connections.length === 0 ? (
            <div className="text-center py-8 px-4 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-3 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">Nenhuma conversa disponível</h3>
              <p className="text-gray-600 mb-4 dark:text-gray-400">
                Você ainda não tem amigos para iniciar uma conversa.
                Adicione amigos primeiro para poder trocar mensagens.
              </p>
              <Link
                to="/connections"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                Gerenciar amigos
              </Link>
            </div>
          ) : filteredConnections.length === 0 ? (
            <div className="text-center py-8 px-4 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
              <Search className="mx-auto h-12 w-12 text-gray-400 mb-3 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">Nenhum resultado</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Não encontramos nenhum amigo correspondente à sua pesquisa.
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden divide-y dark:border-gray-700 dark:divide-gray-700">
              {filteredConnections.map(connection => {
                const profile = connection.connected_profile;
                const userId = profile?.id || '';
                const unreadCount = unreadCountMap[userId] || 0;
                
                return (
                  <div 
                    key={connection.id} 
                    className="flex items-center p-4 hover:bg-gray-50 cursor-pointer dark:hover:bg-gray-700/50"
                    onClick={() => handleStartConversation(profile?.username)}
                  >
                    <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-800 mr-4 dark:bg-indigo-900/30 dark:text-indigo-300">
                      {profile?.avatar_url ? (
                        <img 
                          src={profile.avatar_url} 
                          alt={profile.username || 'Usuário'} 
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-6 w-6" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate dark:text-gray-100">
                        {profile?.full_name || profile?.username || 'Usuário'}
                      </p>
                      <p className="text-sm text-gray-500 truncate dark:text-gray-400">
                        @{profile?.username || 'usuario'}
                      </p>
                    </div>
                    
                    <div className="flex items-center">
                      {unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full mr-2 dark:bg-red-600">
                          {unreadCount}
                        </span>
                      )}
                      <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MessagesPage; 